<template>
	<v-menu :model-value="menu.open"
		:activator="menu.activator"
		location="center"
		persistent
		:close-on-content-click="false"
		content-class="elevation-24"
		min-width="370">
		<v-card>
			<v-card-actions class="justify-space-between pb-0">
				<div class="d-inline-flex flex-column">
					<v-card-subtitle v-if="asset.Artist" class="text-caption-1 pa-0">
						{{ asset.Artist }}
					</v-card-subtitle>

					<v-card-title v-if="asset.Title" class="text-h5 text-truncate pa-0 justify-space-between">
						<div class="text-truncate" style="max-width: 150px;">
							{{ asset.Title }}
						</div>
					</v-card-title>
				</div>

				<div class="text-h5"
					:style="!!asset.Artist ? 'position: relative; top: 7px;' : ''">
					{{ secondsToFormat(duration, "hh:mm:ss", {trim: "left", stopTrim: "m"}) }} | {{
						secondsToFormat(
							intro,
							"mm:ss"
						)
					}}
				</div>

				<div>
					<v-btn icon="mdi-close" @click="stopAudioWaveform();" density="comfortable"/>
				</div>
			</v-card-actions>
			<v-card-text class="pa-2">
				<v-container class="pa-0" style="background-color: #efefef;">
					<v-row no-gutters align="center">
						<v-col cols="auto"
							style="background-color: #dadada;border-radius: 3px;">
							<v-btn v-if="!audioWaveformPlaying"
								:disabled="!waveformLoaded"
								variant="flat"
								size="x-small"
								rounded="0"
								height="45"
								width="24"
								style="min-width: 24px;"
								@click.stop="playAudioWaveform">
								<v-icon>mdi-play</v-icon>
							</v-btn>
							<v-btn v-if="audioWaveformPlaying"
								variant="flat"
								size="x-small"
								rounded="0"
								height="45"
								width="24"
								style="min-width: 24px;"
								@click.stop="pauseAudioWaveform">
								<v-icon>mdi-pause</v-icon>
							</v-btn>
						</v-col>
						<v-col>
							<div class="waveform-container"
								:id="`${parentComponent}-waveform-container-${event.EventID ? event.EventID : asset.AssetID}`">
								<Loader :value="!waveformLoaded" :size="45"/>
							</div>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>
		</v-card>
	</v-menu>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import MarkersPlugin from "wavesurfer.js/dist/plugin/wavesurfer.markers.min";
import WaveSurfer from "wavesurfer.js";

/* Modules */
import Loader from "@/components/Loader.vue";

/* Mixins */
import AssetPropertiesMixin from "@/mixins/AssetProperties";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";

export default {
	name: "AuditionAudioWidget",

	components: {
		Loader
	},

	mixins: [AssetPropertiesMixin, DurationFormatMixin],

	props: ["menu", "event", "parentComponent"],

	emits: ["close-menu"],

	data() {
		return {
			audioWaveformPlaying: false,
			waveform: null,
			waveformLoaded: false
		}
	},

	computed: {
		asset() {
			return this.event?.Asset ?? this.event;
		},

		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		activeStudioAuditionOutputDevice() {
			return this.$store.getters.getActiveStudioAuditionOutputDevice();
		},

		clientId() {
			return this.$store.getters.clientId();
		}
	},

	methods: {
		initWaveform(audioSource) {
			if (!this.waveform) {

				const markers = [];
				// Intro marker
				if (!_.isNil(this.intro)) {
					markers.push({
						time: this.intro
					});
				}
				// Outro marker
				if (!_.isNil(this.outro)) {
					markers.push({
						time: this.outro
					});
				}

				this.waveform = WaveSurfer.create({
					container: `#${this.parentComponent}-waveform-container-${this.event.EventID ??
																			  this.asset.AssetID}`,
					partialRender: true,
					height: 45,
					waveColor: "rgba(0, 44, 198, 0.34)",
					cursorColor: "transparent",
					cursorWidth: 2,
					barHeight: 0.7,
					hideScrollbar: true,
					plugins: [
						MarkersPlugin.create({
							markers: markers
						})
					]
				});
				if (audioSource instanceof ArrayBuffer) {
					this.waveform.loadArrayBuffer(audioSource);
				}
				else {
					this.waveform.load(audioSource);
				}

				this.waveform.on("ready", () => {
					this.waveformLoaded = true;
					this.playAudioWaveform();
				});

				this.waveform.on("error", () => {
					this.$emit("close-menu");
					this.$store.commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{message: "Audio format not supported or source not found."}
					)
				});

				this.waveform.on("finish", () => {
					this.audioWaveformPlaying = false;
				});

				this.waveform.on("destroy", () => {
					this.waveform = null;
				});
			}
			else {
				this.playAudioWaveform();
			}
		},

		playAudioWaveform() {
			this.setAudioOutDevice();

			this.audioWaveformPlaying = true;
			this.waveform.setCursorColor("#002193");
			this.waveform.play();
		},

		pauseAudioWaveform() {
			this.audioWaveformPlaying = false;
			this.waveform.pause();
		},

		stopAudioWaveform() {
			this.audioWaveformPlaying = false;
			this.$emit("close-menu");
			if (this.waveformLoaded) {
				this.waveform.stop();
			}
			else {
				this.waveform?.destroy();
			}
		},

		setAudioOutDevice() {
			this.waveform.setSinkId(this.activeStudioAuditionOutputDevice?.device ?? "default");
		}
	},

	watch: {
		"menu.open"(open) {
			if (!open) {
				this.stopAudioWaveform();
			}
		},

		activeStudioAuditionOutputDevice() {
			if (this.waveform) {
				this.setAudioOutDevice();
			}
		}
	},

	mounted() {
		this.auditionAudioWidgetMidiBC = new BroadcastChannel(`audition-audio-widget-midi-channel-${this.clientId}`);
		this.auditionAudioWidgetMidiBC.addEventListener("message", (message) => {
			switch (message.data.type) {
				case "destroyAuditionAudio":
					this.stopAudioWaveform();
					break;
			}
		});
	},

	beforeUnmount() {
		this.auditionAudioWidgetMidiBC.postMessage({
			type: "closeAuditionAudio"
		});
		this.auditionAudioWidgetMidiBC.close();

		if (this.waveform) {
			this.stopAudioWaveform();
		}
	}
}
</script>

<style scoped lang="scss">
:deep(.waveform-container) {
	position: relative;

	.wavesurfer-marker {
		> div:first-child {
			background: #fc833e !important;
			opacity: 1 !important;
		}

		> div:last-child {
			display: none !important;;
		}
	}
}
</style>